<template>
	<div>
        <div class="flex justify-between mb-5 border-b border-gray-200 pb-2">
            <div class="flex items-center justify-between w-full">
                <h1 class="module-header">Comments</h1>
            </div>
        </div>
        <div class="flex">
        	<div class="w-3/5 pr-10 border-r border-dotted border-gray-300 space-y-5">
				<div v-for="comment in comments.filtered" :key="comment.id" class="p-5 odd:bg-gray-50">
					<div v-if="! comment.is_internal || $gate.isAdmin()" class="w-full">
						<div class="mb-2">
							<div class="flex items-center justify-between">
								<div class="flex items-center space-x-2">
									<span v-if="comment.author.avatar" class="flex items-center justify-center" v-html="comment.author.avatar"></span>
									<span class="text-base text-blue-600 font-semibold">{{ comment.author.name }}</span>
									<span v-if="comment.is_internal" class="flex items-center justify-center" title="Internal Comment">
										<inline-svg name="locked"></inline-svg>
									</span>
									<span class="text-sm">{{ comment.posted_at }}</span>
								</div>
								<div v-if="$gate.isAdmin() || comment.author.id == $gate.user.id" class="space-x-1">
									<button type="button" @click="openEditComment(comment.id)">
                                        <inline-svg name="edit-pencil" classes="w-5 h-5 cursor-pointer"></inline-svg>
									</button>
									<button @click="confirmDeleteComment(comment.id)" type="button">
										<inline-svg name="trash"></inline-svg>
									</button>
								</div>
							</div>
						</div>
						<div class="text-base leading-normal whitespace-pre-line">{{ comment.comment }}</div>
						<div v-if="comment.attachment" class="mt-4 ml-4 shadow p-4 even:bg-white odd:bg-gray-50 border-t-2 border-indigo-500 rounded-md">
							<div class="flex items-center">
								<span class="flex items-center justify-center w-8 h-8" v-html="comment.attachment.icon"></span>
								<div class="flex w-full items-center">
									<div class="flex flex-1 flex-col ml-2">
										<p class="text-sm font-semibold">{{ comment.attachment.name | truncate(65) }}</p>
										<p v-if="comment.attachment.description" class="text-sm mt-1">{{ comment.attachment.description }}</p>
									</div>
									<div class="flex">
										<div v-if="comment.attachment.is_image">
				                            <a :href="`#${comment.attachment.id}`">
				                                <inline-svg name="show"></inline-svg>
				                            </a>
				                            <a href="#_" class="lightbox" :id="`${comment.attachment.id}`">
				                                <img :src="comment.attachment.view_url">
				                            </a>
				                        </div>
		                                <a v-else :href="comment.attachment.preview_url" target="_blank" title="Open Document">
		                                    <inline-svg name="show"></inline-svg>
		                                </a>
		                                <a class="ml-2" :href="comment.attachment.download_url" title="Download Document">
		                                    <inline-svg name="download"></inline-svg>
		                                </a>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
		        <div v-if="this.comments.filtered.length == 0">
		            <p class="none-found">No Comments Found</p>
		        </div>
		    </div>
	        <div class="w-2/5 p-6 pt-0">
				<h2 class="text-lg mb-3">Post New Comment</h2>

	        	<textarea v-model="newComment.comment" class="form-textarea w-full h-48 mb-6"></textarea>

				<div v-if="$gate.isAdmin()" class="mb-6">
					<div class="flex justify-between items-center">
						<div class="flex flex-col w-2/3">
							<p class="text-sm font-semibold mb-1">Internal Comment?</p>
							<p class="text-xs leading-normal">If turned on, this comment will only be viewable by system administrators</p>
						</div>
						<toggle-button v-model="newComment.is_internal" color="#38C172" sync></toggle-button>
					</div>
				</div>

	        	<div class="rounded-b shadow p-4 border-t-2 border-indigo-500">
	        		<h3 class="text-sm mb-5">Add attachment</h3>

	        		<div class="flex mb-4">
		        		<div class="flex flex-col mr-4">
			        		<label for="category" class="text-xs uppercase font-semibold mb-2">Category</label>
			        		<select v-model="selectedCategory" name="category" class="form-select" @change="newComment.attachment_id = ''">
			        			<option value="">Select Category</option>
			        			<option v-for="category in this.documents.categories" :key="category.id" :value="category.id">{{ category.name }}</option>
			        		</select>
			        	</div>

		        		<div class="flex flex-col" v-if="selectedCategory != ''" name="subcategory">
							<label for="subcategory" class="text-xs uppercase font-semibold mb-2">Subcategory</label>
			        		<select v-model="selectedSubcategory" class="form-select" @change="newComment.attachment_id = ''">
			        			<option value="">Select Subcategory</option>
			        			<option v-for="subcategory in availableSubcategories" :key="subcategory.id" :value="subcategory.id">{{ subcategory.name }}</option>
			        		</select>
		        		</div>
		        	</div>

	        		<div class="flex flex-col" v-if="availableDocuments.length" name="document">
						<label for="document" class="text-xs uppercase font-semibold mb-2">Document</label>
		        		<select v-model="newComment.attachment_id" class="form-select">
		        			<option value="">Select Document</option>
		        			<option v-for="file in availableDocuments" :key="file.id" :value="file.id">{{ file.name }}</option>
		        		</select>
		        	</div>
		        	<div v-if="selectedCategory != '' && ! availableDocuments.length" class="mt-6">
		        		<div class="text-sm text-center">No Documents Found</div>
		        	</div>
	        	</div>

	        	<button class="btn btn-green float-right mt-4" @click="saveComment">Save Comment</button>
	        </div>
		</div>

		<notifications classes="notification" position="bottom right"></notifications>

        <modal name="edit-comment" width="50%" height="auto" draggable=".modal-header">
            <edit-comment
                :commentId="selectedCommentId"
                @comment-updated="commentUpdated">
            </edit-comment>
        </modal>
	</div>
</template>

<script>
	import { mapState } from 'vuex';

	export default {
	    name: 'ReoptComments',

        props: {
            data: {
                type: [Array, Object],
                required: true
            },

            documentData: {
                type: [Array, Object],
                required: true
            },

            commentableId: {
                type: Number,
                required: true
            },

            commentableType: {
                type: String,
                required: true
            },
        },

        data() {
        	return {
        		newComment: {
        			commentable_id: this.commentableId,
        			commentable_type: this.commentableType,
        			author_id: window.user.id,
        			comment: '',
        			attachment_id: '',
        			is_internal: false
        		},
        		selectedCommentId: null,
        		selectedCategory: '',
        		selectedSubcategory: '',
        	}
        },

        mounted() {
            this.comments.all = this.data;
            this.comments.filtered = this.data;

            this.documents.all = this.documentData;
            this.documents.filtered = this.documentData;

            this.$store.dispatch('loadCategories');
        },

		computed: {
			...mapState(['sites']),
			...mapState(['comments']),
			...mapState(['documents']),

			availableSubcategories() {
				return this.documents.subcategories.filter(subcategory => {
					return subcategory.category_id == this.selectedCategory;
				});
			},

			availableDocuments() {
				return this.documents.all.filter(file => {
					if (this.selectedSubcategory != '') {
						return file.category_id == this.selectedCategory && file.subcategory_id == this.selectedSubcategory
					} else {
						return file.category_id == this.selectedCategory;
					}
				});
			},
		},

		methods: {
			openEditComment(commentId) {
				this.selectedCommentId = commentId;

				this.$modal.show('edit-comment');
			},

			saveComment() {
				axios.post('/comments', this.newComment).then(response => {
					this.comments.filtered.unshift(response.data.comment);
					this.sites.selectedSite.comments = this.comments.all;

					this.resetForm();

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });
				}).catch(error => {
                    this.$notify({
                        type: 'error',
                        title: 'Error',
                        text: error.response.data.message
                    });
				})
			},

            confirmDeleteComment(commentId) {
                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this comment?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteComment(commentId);
                    }
                });
            },

            deleteComment(commentId) {
                axios.delete('/comments/' + commentId)
                    .then(response => {
                    	this.comments.filtered = _.remove(this.comments.filtered, comment => { return comment.id != commentId });
                    	this.sites.selectedSite.comments = this.comments.all;

                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: 'Comment Deleted'
                        });
                    })
                    .catch(error => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },

			commentUpdated(commentData) {
                this.comments.filtered = _.remove(this.comments.filtered, (comment) => { return comment.id != commentData.id });
                this.comments.filtered.unshift(commentData);
			},

			resetForm() {
				this.newComment.comment = '';
				this.newComment.attachment_id = '';
				this.selectedCategory = '';
				this.selectedSubcategory = '';
			}
		},
	}
</script>
